import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Modal,
  DefaultButton,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Text,
} from "@fluentui/react";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@fluentui/react-components";
import { styles } from "../../../Shared/shared.confirmationModel.styles";
import { styles as sharedStyles } from "../../../Shared/shared.styles";
import {
  ExperimentConfigType,
  ExperimentType,
  ExtensionDetailsType,
  PullRequestChange,
} from "../../../Types";
import { useToast } from "../../../Contexts/ToastContext";
import {
  createAdoBranch,
  createAdoPullRequest,
  pushToAdoBranch,
} from "../../../Api/ado.api";
import { useEv2ExperimentParams } from "../../../Hooks/UseCurrentExtension";
import { useNavigateEv2Experiments } from "../../../Hooks/UseNavigateToEv2Experiments";
import ConfirmationModelPrSection from "./ConfirmationModelPrSection";
import { REPO_URL } from "../../../Shared/shared.consts";
import { useGetBranchDetails } from "../../../Hooks/UseGetBranchDetails";
import useAppInsights from "../../../Hooks/UseAppInsights";
import { OperationTracker } from "../../../Hooks/UseAppInsights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { NavType } from "../../../Shared/AppInsights";

interface ExperimentDetailsConfirmationModelProps {
  /**
   * Whether the modal is open and visible
   */
  isOpen: boolean;
  /**
   * Whether this is creating a new experiment (true) or updating existing (false)
   */
  isCreate?: boolean;
  /**
   * The extension key to use as prefix for featureKey if enableExtensionPrefix is true
   */
  extensionPrefix?: string;
  /**
   * Callback used when user cancels the operation
   */
  onCancel: () => void;
  /**
   * The experiment form data to be saved or updated
   */
  formData: ExperimentType;
}

/**
 * A confirmation modal for creating or updating experiments
 * Displays experiment configuration details and handles Azure DevOps branch/PR creation
 */
const ExperimentDetailsConfirmationModel: React.FC<
  ExperimentDetailsConfirmationModelProps
> = ({ isCreate = false, extensionPrefix = "", onCancel, formData }) => {
  const { showToast } = useToast();
  const isMountedRef = useRef(true);
  const { navigateToRoot } = useNavigateEv2Experiments();
  const { extensionFolder, extensionName } = useEv2ExperimentParams();
  const {
    trackNavigationEvent,
    trackOperationEvent,
    trackExceptionEvent,
    createOperationTracker,
  } = useAppInsights("ExperimentDetailsConfirmationModel");
  const [error, setError] = useState<string | null>("");
  const [isLoading, setLoading] = useState(false);
  const prUrl = formData.prUrl || REPO_URL;
  const prExists = Boolean(formData.prUrl);

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { branchName, branchUrl } = useGetBranchDetails({
    branchNameModifier: formData.featureKey,
  });

  const configJson = useMemo(() => {
    // base configs are now optional, but we'll include them if they were previously used
    const extensionConfig: ExtensionDetailsType | null = formData.displayName
      ? {
          displayName: formData.displayName || "",
          secretId: formData.secretId || "",
          assignmentUnit: formData.assignmentUnit || "muid",
          environment: formData.environment || "Prod",
          serviceIdentifier: formData.serviceIdentifier || "",
          serviceGroup: formData.serviceGroup || "",
          tenantId: formData.tenantId || "",
          subscriptionKey: formData.subscriptionKey || "",
          applicationId: formData.applicationId || "",
        }
      : null;
    const config: ExperimentConfigType = {
      ...(extensionConfig && extensionConfig),
      featureKey: formData.enableExtensionPrefix
        ? `${extensionPrefix}${formData.featureKey}`
        : formData.featureKey || "",
      featureName: formData.featureName || "",
      featureDescription: formData.featureDescription || "",
      ...(formData.extensionFilters && {
        extensionFilters: formData.extensionFilters,
      }),
      analysisTags: formData.analysisTags || [[], [], [], [], [], []],
      // Include variant details if they exist
      ...(formData.variantDetails?.length && {
        variantDetails: formData.variantDetails,
      }),
      // Include advanced filters if they exist
      ...(formData.tenantFilters && { tenantFilters: formData.tenantFilters }),
      ...(formData.extensionVersionFilters && {
        extensionVersionFilters: formData.extensionVersionFilters,
      }),
      ...(formData.environmentFilters && {
        environmentFilters: formData.environmentFilters,
      }),
    };
    return JSON.stringify(config, null, 4);
  }, [formData, extensionPrefix]);

  const onSave = async (tracker: OperationTracker) => {
    if (!branchName) {
      throw new Error("Branch name is missing");
    }
    if (!extensionFolder) {
      throw new Error("Extension name is missing");
    }
    try {
      // Create branch if pr is not already created
      if (!formData.prUrl) {
        await createAdoBranch(branchName, "main");
        tracker.step("BranchCreated", { branchName });
      }

      // Push files to the branch
      const filename = isCreate
        ? `${formData.featureKey}.json`
        : formData.__filename;
      const extensionPath = extensionFolder.extensionRepoPath;
      const itemPath = isCreate
        ? `/Extensions/${extensionPath}/${filename}`
        : formData.__fullpath;

      const changes: PullRequestChange[] = [
        {
          changeType: (isCreate ? "add" : "edit") as "add" | "edit",
          itemPath,
          newContent: configJson,
          contentType: "rawtext" as const,
        },
      ];
      const commitMessage = `${
        isCreate ? "Add" : "Update"
      } experiment files for ${filename}`;
      await pushToAdoBranch({ branchName, commitMessage, changes });
      tracker.step("FilesPushed", { branchName, commitMessage });

      // Create pull request if not already created
      if (!formData.prUrl) {
        const prTitle = `${
          isCreate ? "Add" : "Update"
        } experiment: ${filename}`;
        const prDescription = `Automated PR for ${filename} (${
          isCreate ? "creation" : "update"
        }).`;
        await createAdoPullRequest({
          sourceBranch: branchName,
          targetBranch: "main",
          title: prTitle,
          description: prDescription,
        });
        tracker.step("PullRequestCreated", { branchName });
      }
    } catch (err) {
      const errorMessage =
        "Failed to create branch or push files: " +
        (err instanceof Error ? err.message : String(err));
      console.error(errorMessage);
      trackExceptionEvent(errorMessage, SeverityLevel.Error, {
        function: "onSave",
      });
      throw err;
    }
  };

  const handleCreate = async () => {
    if (!isMountedRef.current) return;

    setLoading(true);
    setError("");
    const tracker = createOperationTracker(
      isCreate ? "CreateExperiment" : "UpdateExperiment",
      "handleCreate",
    );
    tracker.start({ formData, branchName, isCreate, prExists });
    try {
      await onSave(tracker);

      if (!isMountedRef.current) return;
      tracker.succeed();
      showToast({
        message: `${isCreate ? "Creation" : "Update"} of ${
          formData.featureName
        } was successful.`,
        prUrl,
        type: "success",
        duration: 5000,
        dismissible: true,
      });

      navigateToRoot();
      trackNavigationEvent({
        destination: "ExperimentsTable",
        navType: NavType.Internal,
        function: "handleCreate",
        properties: { formData, prUrl },
      });
    } catch (err: any) {
      if (!isMountedRef.current) return;
      tracker.fail(err.message);
      setError(err.message);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleOnCancel = () => {
    onCancel();
    trackOperationEvent({
      operation: "Cancel",
      function: "handleOnCancel",
      properties: { formData, prUrl },
    });
  };

  return (
    <Modal isOpen={true} onDismiss={handleOnCancel} isBlocking={true}>
      <div className={styles.confirmationModalContent}>
        <h2>{isCreate ? "Create experiment" : "Update experiment"}</h2>
        <Text>
          {prExists
            ? `This will push and update to the following PR:`
            : "This will create the following pull request. Link to pull request will be provided upon creation."}
        </Text>
        {error && (
          <Accordion
            defaultOpenItems={["error"]}
            collapsible
            onClick={() => {
              trackOperationEvent({
                operation: "AccordionToggle",
                function: "Accordion.Error.onClick",
                properties: { error, formData, prUrl },
              });
            }}
          >
            <AccordionItem value="error">
              <AccordionHeader
                className={styles.confirmationModalAccordionHeader}
              >
                Failed to {isCreate ? "create" : "update"} experiment
              </AccordionHeader>
              <AccordionPanel>
                <div className={styles.confirmationModalErrorBox}>{error}</div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
        <ConfirmationModelPrSection
          configJson={configJson}
          configJsonFilename={`${formData.featureKey}.json`}
          prTitle={`[${extensionName}] [${formData.featureKey}] ${formData.featureName} New Experiment`}
          prUrl={prUrl}
          branchUrl={prExists ? branchUrl : undefined}
          branchName={branchName}
        />
        <div className={styles.confirmationModalActions}>
          <DefaultButton onClick={handleOnCancel} disabled={isLoading}>
            No, cancel
          </DefaultButton>
          <PrimaryButton onClick={handleCreate} disabled={isLoading}>
            {isLoading
              ? isCreate
                ? "Creating..."
                : "Updating..."
              : isCreate
                ? "Create experiment"
                : "Update experiment"}
          </PrimaryButton>
        </div>
        {isLoading && (
          <div className={sharedStyles.spinnerOverlay}>
            <Spinner
              size={SpinnerSize.large}
              label={
                isCreate ? "Creating experiment..." : "Updating experiment..."
              }
              labelPosition="bottom"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ExperimentDetailsConfirmationModel;
